/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { BarChartOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  OUTCOME_LABEL,
  RECORD_OUTCOMES,
  recordBarWidthPercent,
} from '@sportninja/common/features/competition/operations/utils/recordBarSegments'
import type { StandingsRow } from '@sportninja/common/features/competition/operations/types/standings.types'
import {
  font,
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import GridEmptyState from '../GridEmptyState'
import RecordBar, { EmptyRecordTrack } from './RecordBar'
import RecordLegend from './RecordLegend'

const translate = t as (key: string, options?: unknown) => string

interface DivisionStandingsProps {
  rows: StandingsRow[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

/**
 * The Standings tab body: one proportional record bar per team.
 *
 * Bar LENGTH encodes games played (scaled to the division's busiest team) and
 * segment widths encode the W/L/T/OTL/SOL split. Every row also prints its
 * record as text, so colour is never the only channel.
 */
const DivisionStandings = ({
  rows,
  isLoading,
  isError,
  onRetry,
}: DivisionStandingsProps) => {
  'use memo'

  if (isLoading) {
    return (
      <div css={cardStyle}>
        <div css={headStyle}>
          <span css={titleStyle}>
            {capitalizeWords(translate('common:inDivisionRecord'))}
          </span>
        </div>
        <div css={bodyStyle}>
          {[0, 1, 2, 3].map((row) => (
            <div key={row} css={skeletonRowStyle}>
              <SkeletonText width='38%' />
              <SkeletonText width='100%' />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div css={cardStyle}>
        <div css={messageStyle}>
          <span>{translate('common:standingsLoadFailed')}</span>
          <button type='button' css={retryStyle} onClick={onRetry}>
            {capitalizeWords(translate('common:retry'))}
          </button>
        </div>
      </div>
    )
  }

  const played = rows.filter((row) => row.record)

  // Every team returned "0" — the division exists but nothing has been played.
  if (played.length === 0) {
    return (
      <GridEmptyState
        icon={<BarChartOutlined />}
        title={capitalizeWords(translate('common:noGamesPlayedYet'))}
        hint={translate('common:standingsAppearAfterGames')}
      />
    )
  }

  const maxGamesPlayed = Math.max(
    ...played.map((row) => row.record?.gamesPlayed ?? 0)
  )

  return (
    <div css={cardStyle}>
      <div css={headStyle}>
        <span css={titleStyle}>
          {capitalizeWords(translate('common:inDivisionRecord'))}
        </span>
        <RecordLegend />
      </div>

      <div css={bodyStyle}>
        {rows.map((row, index) => {
          const label = row.record
            ? RECORD_OUTCOMES.filter((outcome) => row.record?.[outcome])
                .map(
                  (outcome) =>
                    `${row.record?.[outcome]} ${OUTCOME_LABEL[outcome]}`
                )
                .join(', ')
            : translate('common:noGames')

          return (
            <div key={row.teamId} css={rowStyle}>
              <div css={teamCellStyle}>
                <span css={rankStyle}>{index + 1}</span>
                {row.logo ? (
                  <img src={row.logo} alt='' css={logoStyle} />
                ) : (
                  <span css={logoFallbackStyle}>{row.abbreviation}</span>
                )}
                <span css={nameStyle} title={row.name}>
                  {row.name}
                </span>
              </div>

              <div css={trackStyle}>
                {row.record ? (
                  <div
                    style={{
                      width: `${recordBarWidthPercent(
                        row.record.gamesPlayed,
                        maxGamesPlayed
                      )}%`,
                    }}
                  >
                    <RecordBar
                      record={row.record}
                      teamName={row.name}
                      label={label}
                    />
                  </div>
                ) : (
                  <EmptyRecordTrack teamName={`${row.name}: ${label}`} />
                )}
              </div>

              <span css={recordTextStyle(!row.record)}>
                {row.record
                  ? RECORD_OUTCOMES.map(
                      (outcome) => row.record?.[outcome] ?? 0
                    ).join('-')
                  : translate('common:noGames')}
              </span>

              <span css={pointsStyle(row.points === 0)}>{row.points}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const cardStyle = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  overflow: hidden;
`

const headStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.SECONDARY_300};
`

const titleStyle = css`
  ${font.title}
  ${paragraphXSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  flex: none;
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  padding: 4px 16px 12px;
`

const rowStyle = css`
  display: grid;
  grid-template-columns: minmax(140px, 210px) 1fr 92px 34px;
  align-items: center;
  gap: 14px;
  padding: 8px 0;
  border-bottom: 1px solid ${colors.SECONDARY_300};

  &:last-of-type {
    border-bottom: none;
  }
`

const skeletonRowStyle = css`
  display: grid;
  grid-template-columns: 210px 1fr;
  gap: 14px;
  padding: 9px 0;
`

const teamCellStyle = css`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
`

const rankStyle = css`
  ${font.body}
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_100};
  width: 13px;
  flex: none;
  text-align: right;
  font-variant-numeric: tabular-nums;
`

const logoStyle = css`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex: none;
  object-fit: cover;
`

const logoFallbackStyle = css`
  ${font.body}
  ${weightSemiBold}
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
`

const nameStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const trackStyle = css`
  min-width: 0;
`

const recordTextStyle = (isEmpty: boolean) => css`
  ${font.body}
  ${paragraphXSmall}
  ${weightMedium}
  text-align: right;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.03em;
  color: ${isEmpty ? colors.NEUTRAL_100 : colors.NEUTRAL_50};
  ${isEmpty ? 'font-style: italic; letter-spacing: 0;' : ''}
`

const pointsStyle = (isZero: boolean) => css`
  ${font.body}
  ${paragraphXSmall}
  ${weightSemiBold}
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${isZero ? colors.NEUTRAL_100 : colors.NEUTRAL_0};
`

const messageStyle = css`
  ${font.body}
  ${paragraphSmall}
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 20px 16px;
  color: ${colors.NEUTRAL_50};
`

const retryStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightSemiBold}
  background: none;
  border: none;
  padding: 0;
  color: ${colors.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
`

export default DivisionStandings
